import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { SiteLayout, PageHeader } from "../layouts/SiteLayout";
import { ASSETS, BRAND } from "../data/siteContent";
import { ABOUT } from "../constants/siteTestIds";
import { Seo } from "../seo/Seo";
import { buildSchemas, webPageSchema, breadcrumbSchema } from "../seo/schemas";

export default function About() {
  return (
    <SiteLayout>
      <Seo
        title="About Manuel Santana — Media Personality, Podcast Host, Author"
        description="Background, mission and full bio of Manuel Santana — American media personality, host of The Prove Your Point Podcast and author of The 12 Laws of Dating Dynamics."
        path="/about"
        schemas={buildSchemas([
          webPageSchema({ path: "/about", name: "About Manuel Santana", description: "Full bio, background and mission of Manuel Santana." }),
          breadcrumbSchema([{ name: "Home", path: "/" }, { name: "About", path: "/about" }]),
        ])}
      />
      <PageHeader
        overline="About — The Man"
        title="Who is"
        italic="Manuel Santana?"
        kicker="A media personality, podcast host and author building one of the most credible independent voices in modern relationship and culture commentary."
      />

      <section className="bg-white py-20 md:py-28" data-testid={ABOUT.section}>
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 grid grid-cols-12 gap-8 lg:gap-16">
          <aside className="col-span-12 lg:col-span-5">
            <div className="portrait-frame w-full max-w-[560px] aspect-[4/5]">
              <img src={ASSETS.aboutPortrait} alt="Manuel Santana — portrait" draggable={false} />
            </div>
            <p className="mt-5 font-mono-ui text-[10px] tracking-[0.3em] uppercase text-black/40">— Photographed for The Prove Your Point Podcast</p>
            <div className="mt-8 portrait-frame w-full max-w-[560px] aspect-[4/5]">
              <img src={ASSETS.bookingPortrait} alt="Manuel Santana — second portrait" draggable={false} />
            </div>
          </aside>

          <article className="col-span-12 lg:col-span-7 space-y-12">
            <div>
              <p className="font-mono-ui text-[11px] tracking-[0.3em] uppercase text-[#0a0a0a]">— Background</p>
              <p className="mt-5 font-display font-light text-[#0a0a0a] text-2xl md:text-3xl leading-snug">
                He didn't come from media. He built it from a microphone, a notebook and a stubborn refusal to be ordinary.
              </p>
              <div className="mt-6 space-y-5 text-black/70 text-base md:text-lg leading-relaxed">
                <p>
                  Manuel Santana grew up between {BRAND.cities[0]} and {BRAND.cities[3]} — two cities that taught him how to move
                  between rooms. The street-tested instinct of one, the hustle and ambition of the other. He moved through
                  early careers in sales, hospitality and small business before quietly turning a podcast he started in a
                  closet into a media platform of his own.
                </p>
                <p>
                  Today, Manuel splits his time between {BRAND.cities.join(", ")} — interviewing operators,
                  therapists, authors, athletes, ex-spouses and everyday couples on what modern partnership and
                  modern manhood actually feel like up close.
                </p>
              </div>
            </div>

            <div>
              <p className="font-mono-ui text-[11px] tracking-[0.3em] uppercase text-[#0a0a0a]">— Why The Podcast</p>
              <div className="mt-5 space-y-5 text-black/70 text-base md:text-lg leading-relaxed">
                <p>
                  The Prove Your Point Podcast started as a single question: <em>why is everyone yelling at each other about
                  dating, but nobody is teaching anyone how to actually be in a relationship?</em>
                </p>
                <p>
                  Years later, it has grown into one of the most consistent long-form shows on modern
                  relationships, culture and identity — built on a stubborn refusal to flatten either side of a
                  conversation for clicks.
                </p>
              </div>
            </div>

            <div>
              <p className="font-mono-ui text-[11px] tracking-[0.3em] uppercase text-[#0a0a0a]">— Author Journey</p>
              <div className="mt-5 space-y-5 text-black/70 text-base md:text-lg leading-relaxed">
                <p>
                  Manuel's debut book <em>The 12 Laws of Dating Dynamics</em> (2025) found a readership across the modern
                  relationship space and became one of the most quoted titles of the year on dating and partnership.
                </p>
              </div>
            </div>

            <div>
              <p className="font-mono-ui text-[11px] tracking-[0.3em] uppercase text-[#0a0a0a]">— Mission Statement</p>
              <p className="mt-5 font-display text-[#0a0a0a] text-2xl md:text-3xl leading-snug font-light">
                "Give modern people honest language for the part of life everyone keeps fumbling — love, identity, ambition — without flattening anyone in the process."
              </p>
            </div>

            <div className="pt-4 flex flex-wrap gap-4">
              <Link to="/bio" data-testid={ABOUT.readCta} className="btn-primary">
                Read The Full Biography <ArrowRight size={16} />
              </Link>
              <Link to="/press-kit" data-testid={ABOUT.presskit} className="btn-ghost">
                Open The Press Kit
              </Link>
              <Link to="/contact" className="btn-ghost">Book An Interview</Link>
            </div>
          </article>
        </div>
      </section>

    </SiteLayout>
  );
}